// Plan 08-06 — Onboarding counter + signup-skipped + friend-invite-shown.
// Plan 14-05 (MUI-06) — extended with realMatchesPlayed (server-cached
// counter from /api/profile/me) + pushOptInShown (server one-shot flag).
//
// REQ-onboarding-tutorial: the friend-invite suggestion fires AFTER match 3
// (real, post-tutorial) per DEC-027 step 5. This atom is the source of
// truth for the match counter that drives that one-shot.
//
// Plan 06 surface only — Plan 06 wires this into the tutorial-completion +
// signup soft-prompt flow. Plan 09 launch readiness wires
// `incrementMatchesPlayed()` into the real-match completion handler so the
// counter actually advances on real play.
//
// IMPORTANT — TWO counters live in this store (Pitfall 6 in
// 14-RESEARCH.md):
//   • matchesPlayed (Plan 08-06): CLIENT tutorial counter. Bumped on tutorial
//     completion; reset on tutorial replay. Drives the legacy
//     friend-invite-after-3rd-tutorial-match suggestion in
//     FriendInviteSuggestion.tsx.
//   • realMatchesPlayed (Plan 14-05): SERVER-cached counter. Mirror of
//     users.real_matches_played from /api/profile/me. Drives the coach-hint
//     gate (< 5) and the new FriendInviteModal (end-of-3rd-real-match).
// The two counters MUST stay decoupled; merging them would re-enable hints
// for veterans who replay the tutorial.

import { persistentAtom } from '@nanostores/persistent'

export interface OnboardingState {
  matchesPlayed: number
  signupSkipped: boolean
  friendInviteShown: boolean
  // Phase 14 (MUI-06) — server-cached count from /api/profile/me. Separate
  // from `matchesPlayed` tutorial counter (Pitfall 6). The MatchScreen
  // fetches /api/profile/me on mount and refreshes this value after a
  // terminal match completes; downstream UI (CoachOverlay gate,
  // FriendInviteModal trigger) reads from here.
  realMatchesPlayed: number
  // Phase 14 (MUI-06) — server-tracked one-shot. Mirrors
  // users.onboarding_flags.pushOptInShown. When true, the end-of-first-bot-
  // match PushOptInModal does not auto-fire.
  pushOptInShown: boolean
  // POWERUP-02 — client one-shot: the first casual match shows a read-only
  // loadout preview before enqueue so a brand-new player meets their four
  // power-ups. Set true after the preview is shown/dismissed; suppresses it
  // forever (independent of the server realMatchesPlayed cache, which is not
  // seeded on lobby mount).
  casualLoadoutPreviewShown: boolean
  // FLOW-06 — client one-shot: just after the tutorial, before any real match,
  // the lobby nudges a new player toward a safe bot match (BotPracticeCta gets
  // a steer caption). Set true once shown so it never repeats.
  botSteerShown: boolean
}

const ONBOARDING_STORAGE_KEY = 'runegambit.onboarding'

const DEFAULT_STATE: OnboardingState = Object.freeze({
  matchesPlayed: 0,
  signupSkipped: false,
  friendInviteShown: false,
  realMatchesPlayed: 0,
  pushOptInShown: false,
  casualLoadoutPreviewShown: false,
  botSteerShown: false,
})

export const $onboarding = persistentAtom<OnboardingState>(
  ONBOARDING_STORAGE_KEY,
  DEFAULT_STATE,
  {
    encode: (v) => JSON.stringify(v),
    decode: (raw) => {
      if (!raw) return DEFAULT_STATE
      try {
        const parsed = JSON.parse(raw) as Partial<OnboardingState>
        return {
          matchesPlayed:
            typeof parsed.matchesPlayed === 'number' ? parsed.matchesPlayed : 0,
          signupSkipped: parsed.signupSkipped ?? false,
          friendInviteShown: parsed.friendInviteShown ?? false,
          realMatchesPlayed:
            typeof parsed.realMatchesPlayed === 'number'
              ? parsed.realMatchesPlayed
              : 0,
          pushOptInShown: parsed.pushOptInShown ?? false,
          casualLoadoutPreviewShown: parsed.casualLoadoutPreviewShown ?? false,
          botSteerShown: parsed.botSteerShown ?? false,
        }
      } catch {
        return DEFAULT_STATE
      }
    },
  },
)

export function incrementMatchesPlayed(): void {
  const cur = $onboarding.get()
  $onboarding.set({ ...cur, matchesPlayed: cur.matchesPlayed + 1 })
}

export function markSignupSkipped(): void {
  const cur = $onboarding.get()
  $onboarding.set({ ...cur, signupSkipped: true })
}

export function markFriendInviteShown(): void {
  const cur = $onboarding.get()
  $onboarding.set({ ...cur, friendInviteShown: true })
}

// Phase 14 (MUI-06) — server-cache setters. The MatchScreen calls these on
// /api/profile/me load + on flag-POST success so downstream UI sees the
// latest server-authoritative state without an explicit refetch.

export function setRealMatchesPlayedCache(n: number): void {
  const cur = $onboarding.get()
  $onboarding.set({ ...cur, realMatchesPlayed: n })
}

export function setPushOptInShown(): void {
  const cur = $onboarding.get()
  $onboarding.set({ ...cur, pushOptInShown: true })
}

// POWERUP-02 — one-shot flag for the first-casual loadout preview.
export function markCasualLoadoutPreviewShown(): void {
  const cur = $onboarding.get()
  if (cur.casualLoadoutPreviewShown) return
  $onboarding.set({ ...cur, casualLoadoutPreviewShown: true })
}

// FLOW-06 — one-shot flag for the post-tutorial lobby bot-practice steer.
export function markBotSteerShown(): void {
  const cur = $onboarding.get()
  if (cur.botSteerShown) return
  $onboarding.set({ ...cur, botSteerShown: true })
}

export function resetOnboardingState(): void {
  $onboarding.set(DEFAULT_STATE)
}

// ARCA-147 — one-shot sessionStorage baton for deferring the end-of-first-bot-
// match push opt-in OFF the WIN/LOSE/DRAW overlay and INTO the lobby. The bug
// was that MatchScreen fired the PushOptInModal synchronously on
// `runegambit:match-completed`, rendering it over the terminal result overlay.
// Instead, MatchScreen sets this flag when the player navigates HOME out of a
// completed bot match, and LobbyScreen consumes it once on its next mount to
// surface the prompt at a contextually appropriate time. sessionStorage (not
// the persistent onboarding atom) is the right scope: the handoff is a single
// navigation hop, not durable state — the server-tracked `pushOptInShown`
// one-shot remains the authority that gates whether the prompt shows at all.
const PENDING_BOT_PUSH_OPTIN_KEY = 'runegambit:pendingBotPushOptIn'

export function setPendingBotPushOptIn(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(PENDING_BOT_PUSH_OPTIN_KEY, '1')
  } catch {
    // sessionStorage can throw under private-mode / quota limits; the prompt
    // simply won't defer in that case. Non-fatal.
  }
}

// Returns true at most once per handoff, clearing the flag so the prompt fires
// exactly once after a bot-match HOME navigation.
export function consumePendingBotPushOptIn(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    const pending = sessionStorage.getItem(PENDING_BOT_PUSH_OPTIN_KEY) === '1'
    if (pending) sessionStorage.removeItem(PENDING_BOT_PUSH_OPTIN_KEY)
    return pending
  } catch {
    return false
  }
}
